import {
  ERROR_VOLUME_MAX_COUNT,
  ERROR_VOLUME_MAX_SIGNATURES,
  ERROR_VOLUME_SIGNATURE_MAX_LENGTH,
} from './config';
import type {
  ErrorVolumeCounts,
  ErrorVolumeKind,
  ErrorVolumeSnapshot,
} from './types';

/**
 * Module-level window state.
 *
 * Deliberately not React state: counting must work before React mounts and
 * during a render that is itself throwing, which is exactly when an error
 * reporter earns its keep.
 */
let rawCounts: ErrorVolumeCounts = {};
let uniqueCounts: ErrorVolumeCounts = {};
let seenSignatures = new Set<string>();
let windowStartedAtMs = 0;

/**
 * Reduce an error to a stable signature.
 *
 * Approximates what a reporting SDK's deduplication would collapse: same kind,
 * same message, same originating frame. It is an approximation — ours runs on
 * the raw event, theirs runs on a parsed stack — so the unique figure is a
 * guide, not a billing prediction.
 */
function buildSignature(kind: ErrorVolumeKind, detail: string): string {
  const trimmed = detail.slice(0, ERROR_VOLUME_SIGNATURE_MAX_LENGTH);
  return `${kind}|${trimmed}`;
}

/** Describe a thrown value without assuming it is an Error. */
export function describeThrown(thrown: unknown): string {
  if (thrown instanceof Error) {
    const firstFrame = thrown.stack?.split('\n')[1]?.trim() ?? '';
    return `${thrown.name}: ${thrown.message} @ ${firstFrame}`;
  }
  if (typeof thrown === 'string') {
    return thrown;
  }
  // Never JSON.stringify here: a thrown value may be circular, may have a
  // throwing getter, or may be enormous. Any of those would turn the counter
  // into a source of the very errors it counts.
  return Object.prototype.toString.call(thrown);
}

/**
 * Record one occurrence.
 *
 * Safe to call from anywhere, including a global error handler. Never throws.
 */
export function recordErrorVolume(kind: ErrorVolumeKind, detail: string): void {
  if (windowStartedAtMs === 0) {
    windowStartedAtMs = Date.now();
  }

  const currentRaw = rawCounts[kind] ?? 0;
  if (currentRaw < ERROR_VOLUME_MAX_COUNT) {
    rawCounts[kind] = currentRaw + 1;
  }

  const signature = buildSignature(kind, detail);
  if (seenSignatures.has(signature)) {
    return;
  }
  if (seenSignatures.size >= ERROR_VOLUME_MAX_SIGNATURES) {
    return;
  }
  seenSignatures.add(signature);

  const currentUnique = uniqueCounts[kind] ?? 0;
  if (currentUnique < ERROR_VOLUME_MAX_COUNT) {
    uniqueCounts[kind] = currentUnique + 1;
  }
}

/** True when the window holds nothing worth sending. */
export function isErrorVolumeWindowEmpty(): boolean {
  return Object.keys(rawCounts).length === 0;
}

/**
 * Take the current window and start a fresh one.
 *
 * Draining and resetting in one step means a concurrent record() lands in the
 * next window rather than being double-counted or lost.
 */
export function drainErrorVolume(): ErrorVolumeSnapshot {
  const startedAt = windowStartedAtMs === 0 ? Date.now() : windowStartedAtMs;
  const snapshot: ErrorVolumeSnapshot = {
    raw: rawCounts,
    unique: uniqueCounts,
    // Guard the zero case: a window that opened and closed inside the same
    // millisecond would otherwise fail the backend's positive-window check.
    windowMs: Math.max(1, Date.now() - startedAt),
  };

  rawCounts = {};
  uniqueCounts = {};
  seenSignatures = new Set<string>();
  windowStartedAtMs = 0;

  return snapshot;
}

/** Test-only reset; production code drains rather than discards. */
export function __resetForTesting(): void {
  rawCounts = {};
  uniqueCounts = {};
  seenSignatures = new Set<string>();
  windowStartedAtMs = 0;
}
